<script setup>
// NOTICE: KAPTION-INTEGRATION
// Overlay drawer for viewing a full conversation from the Kanban board
// Slides in from the right (2/3rds of screen), contains ConversationBox + ConversationSidebar
// Preloaded: once opened, DOM persists (v-show) so dashboard app iframes don't reload

import { computed, watch, ref, nextTick, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  conversation: {
    type: Object,
    default: null,
  },
  isDarkMode: {
    type: Boolean,
    default: false,
  },
  hasPrev: {
    type: Boolean,
    default: false,
  },
  hasNext: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'prev', 'next']);

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const drawerRef = ref(null);
const openedAt = ref(0);

// Once true, DOM stays alive (v-show hides/shows without destroying iframes)
const hasBeenOpened = ref(false);

// Sidebar visibility driven by the same uiSettings that SidepanelSwitch writes to.
const isSidebarOpen = computed(() => {
  const { is_contact_sidebar_open: isOpen } = uiSettings.value;
  return isOpen ?? true;
});

const currentChat = computed(
  () => store.getters.getSelectedChat || props.conversation || {}
);

// Global Escape key handler (capture phase to beat other listeners)
function handleGlobalEscape(e) {
  if (e.key === 'Escape' && props.isOpen) {
    e.stopPropagation();
    e.preventDefault();
    emit('close');
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleGlobalEscape, true);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalEscape, true);
});

// Watch for cleared conversation state (e.g. ConversationBox back button)
// Guard: only trigger after drawer has been open for at least 500ms
watch(
  () => store.getters.getSelectedChat,
  selectedChat => {
    if (
      props.isOpen &&
      (!selectedChat || !selectedChat.id) &&
      Date.now() - openedAt.value > 500
    ) {
      emit('close');
    }
  }
);

// When drawer opens OR conversation changes while open, load conversation data
watch(
  () => [props.isOpen, props.conversation?.id],
  async ([isOpen, convId], [wasOpen] = []) => {
    if (isOpen && props.conversation) {
      hasBeenOpened.value = true;
      openedAt.value = Date.now();

      // Only reset sidebar on initial open, not on card switch
      if (!wasOpen) {
        updateUISettings({ is_contact_sidebar_open: true });
      }

      if (convId) {
        try {
          await store.dispatch('getConversation', convId);
        } catch {
          // Conversation might already be loaded
        }
      }

      nextTick(() => {
        drawerRef.value?.focus();
      });
    }
  }
);

// No backdrop click handler — backdrop is pointer-events: none so clicks
// pass through to kanban cards underneath, enabling one-click card switching.
</script>

<template>
  <Teleport to="body">
    <!-- v-if creates DOM on first open; v-show keeps it alive after that -->
    <template v-if="hasBeenOpened">
      <Transition name="drawer" appear>
        <div
          v-show="isOpen"
          ref="drawerRef"
          dir="ltr"
          class="kanban-drawer-overlay"
          tabindex="-1"
          @keydown.esc="emit('close')"
        >
          <!-- Backdrop — pointer-events: none so clicks pass through to kanban cards -->
          <div class="kanban-drawer-backdrop" />

          <!-- Nav pill — sits on the left edge of the drawer, sticking out -->
          <div
            class="kanban-drawer-nav"
            :class="isDarkMode ? 'bg-kpt-dark-700' : 'bg-white'"
          >
            <button
              class="kanban-drawer-nav-btn"
              :class="
                isDarkMode
                  ? 'text-kpt-text-secondary-dark hover:text-kpt-text-primary-dark hover:bg-kpt-dark-600'
                  : 'text-kpt-text-secondary-light hover:text-kpt-text-primary-light hover:bg-kpt-light-200'
              "
              :disabled="!hasPrev"
              @click="emit('prev')"
            >
              <i class="i-lucide-chevron-up size-5" />
            </button>
            <button
              class="kanban-drawer-nav-btn"
              :class="
                isDarkMode
                  ? 'text-kpt-text-secondary-dark hover:text-kpt-text-primary-dark hover:bg-kpt-dark-600'
                  : 'text-kpt-text-secondary-light hover:text-kpt-text-primary-light hover:bg-kpt-light-200'
              "
              :disabled="!hasNext"
              @click="emit('next')"
            >
              <i class="i-lucide-chevron-down size-5" />
            </button>
            <div
              class="kanban-drawer-nav-divider"
              :class="isDarkMode ? 'bg-kpt-border-dark' : 'bg-kpt-border-light'"
            />
            <button
              class="kanban-drawer-nav-btn"
              :class="
                isDarkMode
                  ? 'text-kpt-text-secondary-dark hover:text-kpt-text-primary-dark hover:bg-kpt-dark-600'
                  : 'text-kpt-text-secondary-light hover:text-kpt-text-primary-light hover:bg-kpt-light-200'
              "
              @click="emit('close')"
            >
              <i class="i-lucide-x size-5" />
            </button>
          </div>

          <!-- Drawer content - 2/3rds of screen, pinned to the right -->
          <div class="kanban-drawer-content">
            <!-- Conversation content area (same layout as ConversationView) -->
            <div class="kanban-drawer-body">
              <ConversationBox
                :is-contact-panel-open="isSidebarOpen"
                is-on-expanded-layout
                is-inbox-view
              >
                <SidepanelSwitch v-if="currentChat.id" />
              </ConversationBox>
              <!-- v-show keeps iframes alive when sidebar toggled or drawer closed -->
              <ConversationSidebar
                v-show="isSidebarOpen"
                :current-chat="currentChat"
              />
            </div>
          </div>
        </div>
      </Transition>
    </template>
  </Teleport>
</template>

<style scoped lang="scss">
.kanban-drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  justify-content: flex-end;
  outline: none;
  pointer-events: none; // Let clicks pass through to kanban cards underneath
}

.kanban-drawer-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.15);
  pointer-events: none; // Visual-only — clicks pass through
}

.kanban-drawer-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 66.67vw;
  max-width: calc(100vw - 60px); // Never exceed available space minus nav
  z-index: 1;
  background: var(--color-bg-base, #fff);
  overflow: hidden;
  border-left: 1px solid rgba(128, 128, 128, 0.15);
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.1);
  pointer-events: auto; // Drawer itself remains interactive
}

.kanban-drawer-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  // Position to the left of the drawer content (drawer is 66.67vw wide, pinned right)
  right: calc(66.67vw + 0.75rem);
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  border-radius: 9999px;
  padding: 4px;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
  pointer-events: auto; // Nav must be clickable inside pointer-events: none overlay
}

.kanban-drawer-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s ease;

  &:disabled {
    opacity: 0.25;
    cursor: default;
    pointer-events: none;
  }
}

.kanban-drawer-nav-divider {
  width: 1.5rem;
  height: 1px;
  margin: 2px 0;
}

.kanban-drawer-body {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

// Slide-in from RIGHT animation
.drawer-enter-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  .kanban-drawer-backdrop {
    transition: opacity 0.3s ease;
  }

  .kanban-drawer-content {
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
}

.drawer-leave-active {
  transition: all 0.2s ease;

  .kanban-drawer-backdrop {
    transition: opacity 0.2s ease;
  }

  .kanban-drawer-content {
    transition: transform 0.2s ease;
  }
}

.drawer-enter-from {
  .kanban-drawer-backdrop {
    opacity: 0;
  }

  .kanban-drawer-content {
    transform: translateX(100%);
  }
}

.drawer-leave-to {
  .kanban-drawer-backdrop {
    opacity: 0;
  }

  .kanban-drawer-content {
    transform: translateX(100%);
  }
}
</style>
